import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useDispatch } from "react-redux";
import { createKitchen } from "../../../redux/actions/kitchenActions";
import { toast } from "react-hot-toast";
import CameraCapture from "../../../components/shared/CameraCapture";
import { useLanguage } from "../../../context/LanguageContext";

import {
  ArrowLeft,
  Save,
  AlertCircle,
  CheckCircle,
  Upload,
  Camera,
  FileText,
  User,
  Shield,
  Thermometer,
  Heart,
  Users,
  CalendarDays,
  Scissors,
  ShieldCheck,
  Gloves,
} from "lucide-react";

const SectionHPersonalHygiene = () => {
  const navigate = useNavigate();
  const { state } = useLocation();
  const dispatch = useDispatch();
  const { t } = useLanguage();

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [showCamera, setShowCamera] = useState(false);
  const [cameraField, setCameraField] = useState(null);

  const [previewImages, setPreviewImages] = useState({
    h1Photo: null,
    h3Document: null,
  });

  const yesNoToInt = (value) => {
    if (value === t.global.yes) return 1;
    if (value === t.global.no) return 2;
    return null;
  };

  const [form, setForm] = useState({
    h1: "",
    h1Photo: null,
    h2: "",
    h3: "",
    h3Date: "",
    h3Document: null,
    h4: "",
    remarks: "",
  });

  const [errors, setErrors] = useState({});

  // Load saved draft if exists
  useEffect(() => {
    const loadSavedData = () => {
      try {
        const savedData = localStorage.getItem("survey_section_h");
        if (savedData) {
          const parsedData = JSON.parse(savedData);
          const { timestamp, canteenInfo, ...formData } = parsedData;
          setForm(formData);
        }
      } catch (err) {
        console.error("Error loading saved data:", err);
      }
    };

    loadSavedData();
  }, []);

  // Auto-save to localStorage
  useEffect(() => {
    const timer = setTimeout(() => {
      if (Object.values(form).some((v) => v !== "" && v !== null)) {
        localStorage.setItem("survey_section_h", JSON.stringify(form));
      }
    }, 500);

    return () => clearTimeout(timer);
  }, [form]);

  const handleChange = (e) => {
    const { name, value, type, files } = e.target;

    if (type === "file") {
      const file = files[0];

      // Validate file size immediately
      if (file && file.size > 5 * 1024 * 1024) {
        setErrors((prev) => ({
          ...prev,
          [name]: t.global.err_file_size,
        }));
        return;
      }

      // Validate file type
      const allowedTypes = [
        "application/pdf",
        "image/jpeg",
        "image/jpg",
        "image/png",
      ];
      if (file && !allowedTypes.includes(file.type)) {
        setErrors((prev) => ({
          ...prev,
          [name]: t.global.err_file_type_long,
        }));
        return;
      }

      setForm((prev) => ({ ...prev, [name]: file }));
      if (file && file.type.startsWith("image/")) {
        const imageUrl = URL.createObjectURL(file);
        setPreviewImages((prev) => ({
          ...prev,
          [name]: imageUrl,
        }));
      }

      // Clear file error if validation passes
      if (errors[name]) {
        setErrors((prev) => ({ ...prev, [name]: undefined }));
      }
    } else {
      setForm((prev) => ({ ...prev, [name]: value }));
    }

    // Clear error when user makes a selection
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    // Required field validations for all radio buttons
    if (!form.h1) newErrors.h1 = t.global.err_a1;
    if (!form.h2) newErrors.h2 = t.global.err_a1;
    if (!form.h3) newErrors.h3 = t.global.err_a1;
    if (!form.h4) newErrors.h4 = t.global.err_a1;

    // File validation only if file IS uploaded (optional but recommended)
    if (form.h1Photo && form.h1Photo.size > 5 * 1024 * 1024) {
      newErrors.h1Photo = t.global.err_file_size;
    }
    if (form.h1Photo) {
      const allowedTypes = [
        "application/pdf",
        "image/jpeg",
        "image/jpg",
        "image/png",
      ];
      if (!allowedTypes.includes(form.h1Photo.type)) {
        newErrors.h1Photo = t.global.err_file_type_long;
      }
    }

    if (form.h3Document && form.h3Document.size > 5 * 1024 * 1024) {
      newErrors.h3Document = t.global.err_file_size;
    }
    if (form.h3Document) {
      const allowedTypes = [
        "application/pdf",
        "image/jpeg",
        "image/jpg",
        "image/png",
      ];
      if (!allowedTypes.includes(form.h3Document.type)) {
        newErrors.h3Document = t.global.err_file_type_long;
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;
    const isPartial =
      !form.h1 ||
      !form.h2 ||
      !form.h3;

    setIsSubmitting(true);

    try {
      const fd = new FormData();

      // =====================
      // Location & Master IDs
      // =====================
      fd.append("district_id", state?.district_id);
      fd.append("municipality_id", state?.municipality_id);
      fd.append("ward_id", state?.ward_id);
      fd.append("area_id", state?.area_id);
      fd.append("canteen_type_id", state?.canteen_type_id);
      fd.append("reporting_date", new Date().toISOString().split("T")[0]);

      // Add latitude/longitude if available
      if (state?.latitude) fd.append("latitude", state.latitude);
      if (state?.longitude) fd.append("longitude", state.longitude);

      // =====================
      // Section H (Personal Hygiene)
      // =====================
      fd.append("pers_hygn_H1", yesNoToInt(form.h1));
      if (form.h1 === t.global.yes && form.h1Photo) {
        fd.append("pers_hygn_H1_upload", form.h1Photo);
      }

      fd.append("pers_hygn_H2", yesNoToInt(form.h2));

      fd.append("pers_hygn_H3", yesNoToInt(form.h3));
      if (form.h3 === t.global.yes && form.h3Document) {
        fd.append("pers_hygn_H3_upload", form.h3Document);
      }
      if (form.h3 === t.global.yes && form.h3Date) {
        fd.append("pers_hygn_H3_date", form.h3Date);
      }

      fd.append("pers_hygn_H4", yesNoToInt(form.h4));

      fd.append("current_section", "personalhygiene");
      fd.append("reporting_date", new Date().toISOString().split("T")[0]);

      // =====================
      // Dispatch API
      // =====================
      await dispatch(createKitchen(fd));

      // ✅ Save to localStorage with partial flag
      const sectionData = {
        ...form,
        isPartial: isPartial,
        submittedAt: new Date().toISOString(),
      };

      localStorage.setItem("survey_section_h", JSON.stringify(sectionData));

      // ✅ Mark Section H as submitted (could be partial)
      const completedSections = JSON.parse(
        localStorage.getItem("completedSections") || "{}",
      );
      completedSections.h = true;
      localStorage.setItem(
        "completedSections",
        JSON.stringify(completedSections),
      );

      // Show success message
      setShowSuccess(true);

      // Auto-navigate after 2 seconds
      setTimeout(() => {
        setShowSuccess(false);
        navigate("/survey/sections", { state });
      }, 2000);
    } catch (error) {
      console.error("Submission error:", error);

      const sectionData = {
        ...form,
        isPartial: isPartial,
        submittedAt: new Date().toISOString(),
        savedLocally: true,
      };

      localStorage.setItem("survey_section_h", JSON.stringify(sectionData));

      // Mark as completed locally
      const completedSections = JSON.parse(
        localStorage.getItem("completedSections") || "{}",
      );
      completedSections.h = true;
      localStorage.setItem(
        "completedSections",
        JSON.stringify(completedSections),
      );

      toast.error(t.global.alert_saved_locally);
      setIsSubmitting(false);
      navigate("/survey/sections", { state });
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 p-4">
      <div className="max-w-4xl mx-auto">
        {/* Success Message Overlay */}
        {showSuccess && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl p-8 max-w-md w-full text-center animate-scale-in">
              <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
              <h3 className="text-2xl font-bold text-gray-900 mb-2">
                {t.global.success_title}
              </h3>
              <p className="text-gray-600 mb-6">
                Section H submitted successfully.
              </p>
              <div className="flex items-center justify-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                <span className="ml-3 text-gray-600">
                  {t.global.redirecting}
                </span>
              </div>
              <button
                onClick={() => {
                  setShowSuccess(false);
                  navigate("/survey/sections", { state });
                }}
                className="mt-4 px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition"
              >
                {t.global.continue_now}
              </button>
            </div>
          </div>
        )}

        <div className="bg-white rounded-xl shadow p-6">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h2 className="text-2xl font-bold text-gray-800">
                {t.modules.kitchen.sections.H.title}
              </h2>
              <p className="text-gray-600">
                {t.modules.kitchen.sections.H.subtitle}
              </p>
            </div>
            <div className="flex flex-col items-end gap-2">
              <div className="text-sm bg-purple-100 text-purple-800 px-3 py-1 rounded-full flex items-center">
                <User size={14} className="mr-1" />
                Personal Hygiene Survey
              </div>
              <div className="text-sm bg-blue-100 text-blue-800 px-3 py-1 rounded-full">
                {state?.area_name ? `${t.global.canteen_name_label} ${state.area_name}` : t.global.na}
              </div>
              {state?.canteenId && (
                <div className="text-sm bg-purple-100 text-purple-800 px-3 py-1 rounded-full">
                  {t.global.canteen_id_label} {state.canteenId}
                </div>
              )}
            </div>
          </div>

          <div className="space-y-6">
            {/* H.1 - Staff Protective Gear */}
            <div className="border rounded-lg p-4 bg-purple-50 border-purple-200">
              <label className="block font-medium mb-3 flex items-center">
                <ShieldCheck size={18} className="mr-2" />
                H.1 {t.modules.kitchen.sections.H.questions.protectiveGear}
                <span className="text-red-500 ml-1">*</span>
              </label>
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 mb-4">
                <p className="text-sm text-gray-800 font-medium mb-1">
                  {t.modules.kitchen.sections.H.hints.protectiveGear.split(":")[0]}:
                </p>
                <ul className="text-sm text-gray-700 list-disc pl-5 space-y-1">
                  <li className="flex items-start">
                    <span className="font-medium mr-2">
                      Cutting vegetables & washing utensils:
                    </span>
                    <span>Apron and cap</span>
                  </li>
                  <li className="flex items-start">
                    <span className="font-medium mr-2">Cooking:</span>
                    <span>Apron, cap, and mask</span>
                  </li>
                  <li className="flex items-start">
                    <span className="font-medium mr-2">Packaging food:</span>
                    <span>Apron, cap, mask, and food handling gloves</span>
                  </li>
                  <li className="flex items-start">
                    <span className="font-medium mr-2">
                      Cleaning and mopping:
                    </span>
                    <span>Apron and utility gloves</span>
                  </li>
                </ul>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-4">
                {[t.global.yes, t.global.no].map((option) => (
                  <label
                    key={option}
                    className={`flex items-center p-4 border-2 rounded-lg cursor-pointer transition-all ${
                      form.h1 === option
                        ? "bg-blue-100 border-blue-400 shadow-sm"
                        : "bg-white border-gray-200 hover:border-gray-300"
                    } ${errors.h1 ? "border-red-300" : ""}`}
                  >
                    <input
                      type="radio"
                      name="h1"
                      value={option}
                      checked={form.h1 === option}
                      onChange={handleChange}
                      className="mr-3 h-5 w-5"
                      required
                    />
                    <div className="flex items-center">
                      <span
                        className={`text-lg font-medium ${
                          form.h1 === option ? "text-blue-700" : "text-gray-700"
                        }`}
                      >
                        {option}
                      </span>
                    </div>
                  </label>
                ))}
              </div>
              {errors.h1 && (
                <p className="mt-2 text-sm text-red-600 flex items-center">
                  <AlertCircle size={14} className="mr-1" /> {errors.h1}
                </p>
              )}

              {form.h1 === t.global.yes && (
                <div className="mt-6 p-4 bg-green-50 border border-green-200 rounded-lg">
                  <label className="block text-sm font-medium mb-3 text-green-800 flex items-center">
                    <Camera size={14} className="mr-2" />
                    Upload picture of staff wearing PPE (Optional but recommended):
                  </label>

                  {/* Buttons Row */}
                  <div className="grid grid-cols-2 gap-4 mb-4">
                    <button
                      type="button"
                      onClick={() => {
                        setCameraField("h1Photo");
                        setShowCamera(true);
                      }}
                      className="flex items-center justify-center gap-2 p-3 border-2 border-gray-300 text-gray-600 rounded-lg active:scale-95 active:bg-blue-600 active:text-white transition-all duration-150 ease-out font-medium"
                    >
                      <Camera size={20} /> {t.global.useCamera}
                    </button>

                    <label
                      htmlFor="h1Photo"
                      className="flex items-center justify-center gap-2 p-3 border-2 border-gray-300 text-gray-600 rounded-lg active:scale-95 active:bg-blue-600 active:text-white transition-all duration-150 ease-out font-medium cursor-pointer"
                    >
                      <Upload size={20} /> {t.global.uploadPhoto}
                    </label>

                    <input
                      type="file"
                      name="h1Photo"
                      accept=".jpg,.jpeg,.png"
                      onChange={handleChange}
                      className="hidden"
                      id="h1Photo"
                    />
                  </div>

                  {/* Preview */}
                  {form.h1Photo ? (
                    <div className="space-y-3 bg-white p-4 rounded-lg shadow">
                      {previewImages.h1Photo && (
                        <img
                          src={previewImages.h1Photo}
                          alt="Preview"
                          className="max-h-48 mx-auto rounded-lg shadow"
                        />
                      )}

                      <div className="flex justify-between items-center">
                        <p className="text-sm break-all">{form.h1Photo.name}</p>

                        <button
                          type="button"
                          onClick={() => {
                            setForm((prev) => ({ ...prev, h1Photo: null }));
                            setPreviewImages((prev) => ({
                              ...prev,
                              h1Photo: null,
                            }));
                          }}
                          className="text-red-600 text-sm font-medium hover:underline"
                        >
                          {t.global.remove}
                        </button>
                      </div>
                    </div>
                  ) : (
                    <p className="text-sm text-gray-500">{t.global.capture_hint || "No photo selected"}</p>
                  )}
                </div>
              )}
            </div>

            {/* H.2 - Handwashing Practice */}
            <div className="border rounded-lg p-4">
              <label className="block font-medium mb-3 flex items-center">
                <Users size={18} className="mr-2" />
                H.2 {t.modules.kitchen.sections.H.questions.handwashingTraining}
                <span className="text-red-500 ml-1">*</span>
              </label>
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-4">
                <p className="text-sm text-blue-800 font-medium mb-1">
                  {t.modules.kitchen.sections.H.hints.handwashingTraining.split(":")[0]}:
                </p>
                <ol className="text-sm text-blue-700 list-decimal pl-5 space-y-1">
                  <li>Wet hands with clean running water</li>
                  <li>Apply soap and lather</li>
                  <li>Scrub all surfaces for 20 seconds</li>
                  <li>Rinse thoroughly with running water</li>
                  <li>Dry hands with clean towel/air dryer</li>
                  <li>Use towel to turn off faucet</li>
                </ol>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {[t.global.yes, t.global.no].map((option) => (
                  <label
                    key={option}
                    className={`flex items-center p-4 border-2 rounded-lg cursor-pointer transition-all ${
                      form.h2 === option
                        ? "bg-blue-100 border-blue-400 shadow-sm"
                        : "bg-white border-gray-200 hover:border-gray-300"
                    } ${errors.h2 ? "border-red-300" : ""}`}
                  >
                    <input
                      type="radio"
                      name="h2"
                      value={option}
                      checked={form.h2 === option}
                      onChange={handleChange}
                      className="mr-3 h-5 w-5"
                      required
                    />
                    <div className="flex items-center">
                      <span
                        className={`text-lg font-medium ${
                          form.h2 === option ? "text-blue-700" : "text-gray-700"
                        }`}
                      >
                        {option}
                      </span>
                    </div>
                  </label>
                ))}
              </div>
              {errors.h2 && (
                <p className="mt-2 text-sm text-red-600 flex items-center">
                  <AlertCircle size={14} className="mr-1" /> {errors.h2}
                </p>
              )}
            </div>

            {/* H.3 - Health Check-ups */}
            <div className="border rounded-lg p-4 bg-purple-50 border-purple-200">
              <label className="block font-medium mb-3 flex items-center">
                <Heart size={18} className="mr-2" />
                H.3 {t.modules.kitchen.sections.H.questions.healthCheckups}
                <span className="text-red-500 ml-1">*</span>
              </label>
              <p className="text-sm text-gray-600 mb-4">
                {t.modules.kitchen.sections.H.hints.healthCheckups}
              </p>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-4">
                {[t.global.yes, t.global.no].map((option) => (
                  <label
                    key={option}
                    className={`flex items-center p-4 border-2 rounded-lg cursor-pointer transition-all ${
                      form.h3 === option
                        ? "bg-blue-100 border-blue-400 shadow-sm"
                        : "bg-white border-gray-200 hover:border-gray-300"
                    } ${errors.h3 ? "border-red-300" : ""}`}
                  >
                    <input
                      type="radio"
                      name="h3"
                      value={option}
                      checked={form.h3 === option}
                      onChange={handleChange}
                      className="mr-3 h-5 w-5"
                      required
                    />
                    <div className="flex items-center">
                      <span
                        className={`text-lg font-medium ${
                          form.h3 === option ? "text-blue-700" : "text-gray-700"
                        }`}
                      >
                        {option}
                      </span>
                    </div>
                  </label>
                ))}
              </div>
              {errors.h3 && (
                <p className="mt-2 text-sm text-red-600 flex items-center">
                  <AlertCircle size={14} className="mr-1" /> {errors.h3}
                </p>
              )}

              {form.h3 === t.global.yes && (
                <div className="mt-6 space-y-4 p-4 bg-green-50 border border-green-200 rounded-lg">
                  {/* Date Input */}
                  <div>
                    <label className="block text-sm font-medium mb-2 text-gray-700 flex items-center">
                      <CalendarDays size={14} className="mr-2" />
                      Last date of health check-up (Optional but recommended):
                    </label>
                    <input
                      type="date"
                      name="h3Date"
                      value={form.h3Date}
                      onChange={handleChange}
                      className={`w-full border p-3 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 transition ${
                        errors.h3Date ? "border-red-500" : "border-gray-300"
                      }`}
                      max={new Date().toISOString().split("T")[0]}
                    />
                    {errors.h3Date && (
                      <p className="mt-2 text-sm text-red-600 flex items-center">
                        <AlertCircle size={14} className="mr-1" />{" "}
                        {errors.h3Date}
                      </p>
                    )}
                    <p className="text-xs text-gray-500 mt-1">
                      If not entered, section will be marked as "Partially Filled"
                    </p>
                  </div>

                  {/* Document Upload */}
                  <div className="mt-4">
                    <label className="block text-sm font-medium mb-3 text-green-800 flex items-center">
                      <FileText size={14} className="mr-2" />
                      Upload health check-up records (Optional but recommended):
                    </label>

                    <div className="grid grid-cols-2 gap-4 mb-4">
                      <button
                        type="button"
                        onClick={() => {
                          setCameraField("h3Document");
                          setShowCamera(true);
                        }}
                        className="flex items-center justify-center gap-2 p-3 border-2 border-gray-300 text-gray-600 rounded-lg active:scale-95 active:bg-blue-600 active:text-white transition-all duration-150 ease-out font-medium"
                      >
                        <Camera size={20} /> {t.global.useCamera}
                      </button>

                      <label
                        htmlFor="h3Document"
                        className="flex items-center justify-center gap-2 p-3 border-2 border-gray-300 text-gray-600 rounded-lg active:scale-95 active:bg-blue-600 active:text-white transition-all duration-150 ease-out font-medium cursor-pointer"
                      >
                        <Upload size={20} /> {t.global.uploadPhoto}
                      </label>

                      <input
                        type="file"
                        name="h3Document"
                        accept=".pdf,.jpg,.jpeg,.png"
                        onChange={handleChange}
                        className="hidden"
                        id="h3Document"
                      />
                    </div>

                    {/* Preview */}
                    {form.h3Document ? (
                      <div className="space-y-3 bg-white p-4 rounded-lg shadow">
                        {previewImages.h3Document &&
                          form.h3Document.type.startsWith("image/") && (
                            <img
                              src={previewImages.h3Document}
                              alt="Preview"
                              className="max-h-48 mx-auto rounded-lg shadow"
                            />
                          )}

                        <div className="flex justify-between items-center">
                          <p className="text-sm break-all">
                            {form.h3Document.name}
                          </p>

                          <button
                            type="button"
                            onClick={() => {
                              setForm((prev) => ({
                                ...prev,
                                h3Document: null,
                              }));
                              setPreviewImages((prev) => ({
                                ...prev,
                                h3Document: null,
                              }));
                            }}
                            className="text-red-600 text-sm font-medium hover:underline"
                          >
                            {t.global.remove}
                          </button>
                        </div>
                      </div>
                    ) : (
                      <p className="text-sm text-gray-500">
                        {t.global.capture_hint || "No document selected"}
                      </p>
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* H.4 - Personal Hygiene */}
            <div className="border rounded-lg p-4">
              <label className="block font-medium mb-3 flex items-center">
                <Scissors size={18} className="mr-2" />
                H.4 {t.modules.kitchen.sections.H.questions.personalHygiene}
                <span className="text-red-500 ml-1">*</span>
              </label>
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 mb-4">
                <p className="text-sm text-gray-800 font-medium mb-1">
                  {t.modules.kitchen.sections.H.hints.personalHygiene.split(":")[0]}:
                </p>
                <ul className="text-sm text-gray-700 list-disc pl-5 space-y-1">
                  <li>No nail polish or artificial nails</li>
                  <li>Clean, trimmed nails</li>
                  <li>Hair properly covered with cap/headgear</li>
                  <li>Clean uniform/apron</li>
                  <li>No jewelry while handling food</li>
                  <li>Clean shaven or trimmed facial hair</li>
                </ul>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {[t.global.yes, t.global.no].map((option) => (
                  <label
                    key={option}
                    className={`flex items-center p-4 border-2 rounded-lg cursor-pointer transition-all ${
                      form.h4 === option
                        ? "bg-blue-100 border-blue-400 shadow-sm"
                        : "bg-white border-gray-200 hover:border-gray-300"
                    } ${errors.h4 ? "border-red-300" : ""}`}
                  >
                    <input
                      type="radio"
                      name="h4"
                      value={option}
                      checked={form.h4 === option}
                      onChange={handleChange}
                      className="mr-3 h-5 w-5"
                      required
                    />
                    <div className="flex items-center">
                      <span
                        className={`text-lg font-medium ${
                          form.h4 === option ? "text-blue-700" : "text-gray-700"
                        }`}
                      >
                        {option}
                      </span>
                    </div>
                  </label>
                ))}
              </div>
              {errors.h4 && (
                <p className="mt-2 text-sm text-red-600 flex items-center">
                  <AlertCircle size={14} className="mr-1" /> {errors.h4}
                </p>
              )}
            </div>

            {/* Remarks Field (Optional) */}
            <div className="border rounded-lg p-4 bg-blue-50 border-blue-200">
              <label className="block font-medium mb-3 flex items-center">
                <User size={18} className="mr-2" />
                {t.global.remarks}
              </label>
              <textarea
                name="remarks"
                value={form.remarks || ""}
                onChange={handleChange}
                placeholder={t.global.remarksPlaceholder}
                className="w-full border border-gray-300 rounded-lg p-3 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 min-h-[100px]"
                rows={3}
              />
              <p className="text-sm text-gray-500 mt-2">
                {t.global.remarks_placeholder}
              </p>
            </div>

            {/* Optional Fields Notice */}
            <div className="bg-amber-50 border-l-4 border-amber-400 p-4 rounded-r-lg">
              <div className="flex">
                <div className="flex-shrink-0">
                  <AlertCircle className="h-5 w-5 text-amber-500" />
                </div>
                <div className="ml-3">
                  <p className="text-sm text-amber-800">
                    <span className="font-medium">{t.global.optionalNote}:</span> {t.modules.kitchen.sections.H.notes.optionalUploadsNote}
                  </p>
                  <p className="text-xs text-amber-700 mt-2">
                    {t.modules.kitchen.sections.H.notes.healthCheckupRecommendation}
                  </p>
                </div>
              </div>
            </div>

            {/* Hygiene Importance Notice */}
            <div className="bg-green-50 border-l-4 border-green-400 p-4 rounded-r-lg">
              <div className="flex">
                <div className="flex-shrink-0">
                  <Shield className="h-5 w-5 text-green-500" />
                </div>
                <div className="ml-3">
                  <p className="text-sm text-green-800 font-medium">
                    🧼 Personal Hygiene Notice
                  </p>
                  <p className="text-sm text-green-700 mt-1">
                    Proper personal hygiene is critical for food safety. Staff
                    hygiene directly impacts food quality and prevents
                    contamination. Regular training and health check-ups ensure
                    a safe food handling environment.
                  </p>
                </div>
              </div>
            </div>

            {/* Footer Info */}
            <div className="pt-4 border-t border-gray-200">
              <div className="text-center text-sm text-gray-500">
                <span className="text-red-500 font-medium">
                  * Required fields must be completed
                </span>
                <p className="text-sm text-gray-600 mt-1">
                  All other information is optional but recommended for
                  comprehensive personal hygiene assessment.
                </p>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex justify-between pt-6 border-t">
              <button
                type="button"
                onClick={() => navigate("/survey/sections", { state })}
                className="px-6 py-3 bg-gray-200 rounded-lg hover:bg-gray-300 transition font-medium flex items-center"
                disabled={isSubmitting}
              >
                <ArrowLeft size={18} className="mr-2" />
                {t.global.back_to_sections}
              </button>

              <button
                type="button"
                onClick={handleSubmit}
                disabled={isSubmitting}
                className={`px-6 py-3 rounded-lg font-medium flex items-center transition ${
                  isSubmitting
                    ? "bg-blue-400 cursor-not-allowed"
                    : "bg-blue-600 text-white hover:bg-blue-700"
                }`}
              >
                {isSubmitting ? (
                  <>
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                    {t.global.submitting}
                  </>
                ) : (
                  <>
                    <Save size={18} className="mr-2" />
                    {t.global.submit_section_h}
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Custom Animation */}
      <style jsx>{`
        @keyframes scale-in {
          from {
            transform: scale(0.9);
            opacity: 0;
          }
          to {
            transform: scale(1);
            opacity: 1;
          }
        }
        .animate-scale-in {
          animation: scale-in 0.3s ease-out;
        }
      `}</style>
      {showCamera && (
        <CameraCapture
          onCapture={(file) => {
            setForm((prev) => ({
              ...prev,
              [cameraField]: file,
            }));

            const imageUrl = URL.createObjectURL(file);

            setPreviewImages((prev) => ({
              ...prev,
              [cameraField]: imageUrl,
            }));

            setShowCamera(false);
          }}
          onClose={() => setShowCamera(false)}
        />
      )}
    </div>
  );
};

export default SectionHPersonalHygiene;